import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface TDSReportFilters {
  subsidiary_ids?: (number | string)[];
  vendor_id?: number | string;
  line_of_business_id?: number | string;
  from_date?: string; // YYYY-MM-DD
  to_date?: string; // YYYY-MM-DD
  limit?: number;
  offset?: number;
  exclude_void?: boolean;
}

export interface TDSReportRow {
  tb_code: string;
  india_tax_section_code: string;
  tds_gl_code: string;
  period: string;
  transaction_type: string;
  transaction_number: string;
  transaction_date: string;
  invoice_no: string;
  invoice_date: string;
  vendor_customer_code: string;
  vendor_name: string;
  pan_no: string;
  taxable: number;
  rate: number;
  tds: number;
  narration: string;
  expense_code: string;
  expense_name: string;
  vendor_invoice_id: number;
  vendor_invoice_item_id: number;
}

export interface TDSReportResponse {
  status: string;
  message: string;
  data: {
    rows: TDSReportRow[];
    total_rows: number;
    total_pages: number;
    total_taxable: number;
    total_tds: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TDSReportService {
  private readonly API_BASE_URL = environment.accountingUrl;
  private http = inject(HttpClient);

  /**
   * Get TDS report with optional filters
   * Endpoint: GET /api/v1/reports/tds
   */
  getTDSReport(filters: TDSReportFilters): Observable<TDSReportResponse> {
    let params = new HttpParams();

    if (filters.subsidiary_ids && filters.subsidiary_ids.length) {
      params = params.set('subsidiary_ids', filters.subsidiary_ids.join(','));
    }

    if (filters.vendor_id !== undefined && filters.vendor_id !== null) {
      params = params.set('vendor_id', filters.vendor_id.toString());
    }

    if (filters.line_of_business_id !== undefined && filters.line_of_business_id !== null) {
      params = params.set('line_of_business_id', filters.line_of_business_id.toString());
    }

    if (filters.from_date) {
      params = params.set('from_date', filters.from_date);
    }

    if (filters.to_date) {
      params = params.set('to_date', filters.to_date);
    }

    if (filters.limit !== undefined) {
      params = params.set('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      params = params.set('offset', filters.offset.toString());
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get<TDSReportResponse>(`${this.API_BASE_URL}/api/v1/reports/tds/`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Export TDS report to Excel
   * Endpoint: GET /api/v1/reports/tds/export?format=xlsx
   */
  exportToExcel(filters: TDSReportFilters): Observable<Blob> {
    let params = new HttpParams().set('format', 'xlsx');

    if (filters.subsidiary_ids && filters.subsidiary_ids.length) {
      params = params.set('subsidiary_ids', filters.subsidiary_ids.join(','));
    }

    if (filters.vendor_id !== undefined && filters.vendor_id !== null) {
      params = params.set('vendor_id', filters.vendor_id.toString());
    }

    if (filters.line_of_business_id !== undefined && filters.line_of_business_id !== null) {
      params = params.set('line_of_business_id', filters.line_of_business_id.toString());
    }

    if (filters.from_date) {
      params = params.set('from_date', filters.from_date);
    }

    if (filters.to_date) {
      params = params.set('to_date', filters.to_date);
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get(`${this.API_BASE_URL}/api/v1/reports/tds/export`, {
      params,
      responseType: 'blob',
      withCredentials: true
    });
  }

  /**
   * Export TDS report to PDF
   * Endpoint: GET /api/v1/reports/tds/export?format=pdf
   */
  exportToPdf(filters: TDSReportFilters): Observable<Blob> {
    let params = new HttpParams().set('format', 'pdf');

    if (filters.subsidiary_ids && filters.subsidiary_ids.length) {
      params = params.set('subsidiary_ids', filters.subsidiary_ids.join(','));
    }

    if (filters.vendor_id !== undefined && filters.vendor_id !== null) {
      params = params.set('vendor_id', filters.vendor_id.toString());
    }

    if (filters.line_of_business_id !== undefined && filters.line_of_business_id !== null) {
      params = params.set('line_of_business_id', filters.line_of_business_id.toString());
    }

    if (filters.from_date) {
      params = params.set('from_date', filters.from_date);
    }

    if (filters.to_date) {
      params = params.set('to_date', filters.to_date);
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get(`${this.API_BASE_URL}/api/v1/reports/tds/export`, {
      params,
      responseType: 'blob',
      withCredentials: true
    });
  }
}
